import { createContext } from 'react';

/** How loud a toast is, and therefore how it is announced. */
export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger';

/** The one follow-up a toast offers, most often "Undo". */
export interface ToastAction {
  /** The button's label, named for what it does: "Undo", not "OK". */
  readonly label: string;
  /** Called when the button is used. The toast closes itself afterwards. */
  readonly onAction: () => void;
}

/** A request to show a toast. */
export interface ToastRequest {
  /** The message. One sentence; anything longer belongs on the page. */
  readonly message: string;
  /** Defaults to `neutral`. */
  readonly tone?: ToastTone;
  /**
   * A button shown beside the message.
   *
   * This is what makes a reversible action cheap: the change is made at once,
   * and the toast offers to take it back instead of asking first.
   */
  readonly action?: ToastAction;
  /**
   * How long it stays, in milliseconds.
   *
   * Defaults to 6000 for neutral and success, and 10000 when the toast offers
   * an action, because a button that vanishes while someone is reaching for it
   * is worse than none. Warnings and errors never auto-dismiss: WCAG 2.2.1
   * allows a time limit only when the user can extend it, and a message the
   * user needs to act on should not time out at all.
   */
  readonly durationMs?: number;
}

/** What {@link useToast} hands back. */
export interface ToastApi {
  /** Shows a toast and returns its id. */
  readonly show: (request: ToastRequest) => string;
  /** Dismisses a toast early. */
  readonly dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);
